import { CURRICULUM_SLUGS } from '@/constants'
import { TaxoSyllabus } from '@/kontent/taxonomies'
import { Mapping } from '@/types'
import { getSlugByCodename } from './getSlugByCodename'
import { getUrlFromSlugs } from './getUrlFromMapping'

/**
 * Replace KLA slug in URL with canonical KLA from kla_default
 */
const replaceWithCanonicalKla = (url: string, klaDefault?: string) => {
	if (!url || !klaDefault) return url

	const canonicalKlaSlug = getSlugByCodename(klaDefault)
	// Replace the KLA segment (second path segment after /learning-areas/)
	return url.replace(
		/^(\/learning-areas\/)([^/]+)(\/)/,
		`$1${canonicalKlaSlug}$3`,
	)
}

/**
 * @param codename codename of the content
 * @param isCanonicalityChecked check if the mapping is canonical
 * @param isSyllabusCodename check if the codename is a syllabus codename
 */
const getFindByFn =
	(
		codename: string,
		isCanonicalityChecked: boolean,
		isSyllabusCodename: boolean,
	) =>
	(m: Mapping) => {
		const canonicalCondition = CURRICULUM_SLUGS['LEARNING_AREAS'].reduce(
			(_, slug, index) => {
				return m.params?.slug?.[index] === slug
			},
			false,
		)

		let isMatch = false
		if (isSyllabusCodename) {
			isMatch =
				m.params.navigationItem?.codename === codename &&
				(m.params.isCanonical || !isCanonicalityChecked)
		} else {
			// syllabus codename is a TaxoSyllabus
			isMatch =
				m.params.taxoSyllabus === codename &&
				(m.params.isCanonical || !isCanonicalityChecked)
		}

		return isMatch && canonicalCondition
	}

export function getSyllabusUrlFromMappingBySyllabusCodename(
	mappings: Mapping[],
	syllabusCodename: string,
	isCanonicalityChecked = true,
	replaceOverview = false, //by default, syllabus url has /overview. set to true to remove it
) {
	// OC2-5706: Always use false - kla_default handles canonical URL
	const mapping = mappings.find(getFindByFn(syllabusCodename, false, true))

	if (!mapping) {
		return undefined
	}

	let result = getUrlFromSlugs(mapping.params.slug, mapping.params.additional)

	// Replace the KLA slug with the canonical KLA (kla_default)
	// URL structure: /learning-areas/[kla]/[syllabus]/...
	result = replaceWithCanonicalKla(result, mapping.params.kla_default)

	if (result && replaceOverview) {
		return result.replace('/overview', '')
	}
	return result
}

export function getSyllabusUrlFromMappingByTaxo(
	mappings: Mapping[],
	syllabusTaxoCodename: TaxoSyllabus,
	isCanonicalityChecked = true,
	replaceOverview = false, //by default, syllabus url has /overview. set to true to remove it
) {
	// OC2-5706: Always use false - kla_default handles canonical URL
	const mapping = mappings.find(
		getFindByFn(syllabusTaxoCodename, false, false),
	)

	if (!mapping) {
		return undefined
	}

	let result = getUrlFromSlugs(mapping.params.slug)

	// Replace the KLA slug with the canonical KLA (kla_default)
	result = replaceWithCanonicalKla(result, mapping.params.kla_default)

	if (result && replaceOverview) {
		return result.replace('/overview', '')
	}
	return result
}
